import { CATEGORY_DESCRIPTIONS, DEFAULT_CATEGORIES } from '@mrrmlab/shared-types';

export type ModelStatus = 'idle' | 'downloading' | 'loading' | 'ready' | 'error';

export interface ModelProgress {
  status: ModelStatus;
  downloadProgress?: number;
  error?: string;
}

type ProgressListener = (progress: ModelProgress) => void;

// Multilingual sentence embedding model — 45 MB, fits in mobile browser tab.
// Computes cosine similarity between mail text and category labels instead of generating text.
const MODEL_ID = 'Xenova/paraphrase-multilingual-MiniLM-L12-v2';

// Minimum cosine similarity to suggest a category.
const SIMILARITY_THRESHOLD = 0.25;

class MailModelManager {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private pipeline: any | null = null;
  private initPromise: Promise<void> | null = null;
  private currentStatus: ModelProgress = { status: 'idle' };
  private listeners = new Set<ProgressListener>();
  // Cache embeddings of category names so each label is only embedded once.
  private embCache = new Map<string, Float32Array>();

  onProgress(listener: ProgressListener): () => void {
    this.listeners.add(listener);
    listener(this.currentStatus);
    return () => this.listeners.delete(listener);
  }

  isReady(): boolean {
    return this.pipeline !== null;
  }

  async suggest(mailSnippet: string, existingTags: string[]): Promise<string[]> {
    await this.ensureReady();
    if (!this.pipeline) throw new Error('Model not loaded');

    // Merge defaults with user-confirmed tags — this is how the system "learns":
    // every tag the user confirms becomes a future candidate automatically.
    const candidates = [...new Set([...DEFAULT_CATEGORIES, ...existingTags])];
    const cleaned = cleanSnippet(mailSnippet);

    const mailEmb = await this.embed(cleaned);
    const scores = await Promise.all(
      candidates.map(async (cat) => ({
        cat,
        score: dotProduct(mailEmb, await this.embed(CATEGORY_DESCRIPTIONS[cat] ?? cat)),
      }))
    );

    const sorted = [...scores].sort((a, b) => b.score - a.score);
    const tags = sorted.filter((s) => s.score >= SIMILARITY_THRESHOLD).slice(0, 3).map((s) => s.cat);

    console.debug('[MailModel] top scores:', sorted.slice(0, 5).map((s) => `${s.cat}=${s.score.toFixed(3)}`));
    (window as unknown as Record<string, unknown>).__mailModelDebug = { cleaned, scores: sorted.slice(0, 8), tags };
    return tags;
  }

  private async embed(text: string): Promise<Float32Array> {
    const cached = this.embCache.get(text);
    if (cached) return cached;
    const output = await this.pipeline(text, { pooling: 'mean', normalize: true });
    const emb = output.data as Float32Array;
    this.embCache.set(text, emb);
    return emb;
  }

  private async ensureReady(): Promise<void> {
    if (this.pipeline) return;
    if (this.initPromise) return this.initPromise;
    this.initPromise = this.initialize().finally(() => {
      this.initPromise = null;
    });
    return this.initPromise;
  }

  private async initialize(): Promise<void> {
    try {
      this.emit({ status: 'downloading', downloadProgress: 0 });

      // Dynamic import keeps the bundle lean — transformers.js is only loaded
      // on first suggest() call, never at app startup.
      const { pipeline, env } = await import('@huggingface/transformers');

      env.allowRemoteModels = true;

      this.emit({ status: 'loading' });

      this.pipeline = await pipeline('feature-extraction', MODEL_ID, {
        dtype: 'q8',
        progress_callback: (p: { status: string; progress?: number }) => {
          if (p.status === 'downloading' || p.status === 'progress') {
            this.emit({ status: 'downloading', downloadProgress: Math.round(p.progress ?? 0) });
          } else if (p.status === 'loading') {
            this.emit({ status: 'loading' });
          }
        },
      });

      this.emit({ status: 'ready' });
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      const stack = err instanceof Error ? err.stack : undefined;
      console.error('[MailModel] initialization failed:', error, stack ?? JSON.stringify(err, Object.getOwnPropertyNames(err as object)));
      this.emit({ status: 'error', error });
      throw err;
    }
  }

  private emit(progress: ModelProgress): void {
    this.currentStatus = progress;
    for (const l of this.listeners) l(progress);
  }
}

/** Dot product of two normalized vectors = cosine similarity. */
function dotProduct(a: Float32Array, b: Float32Array): number {
  let sum = 0;
  for (let i = 0; i < a.length; i++) sum += (a[i] ?? 0) * (b[i] ?? 0);
  return sum;
}

/** Strips URLs, HTML entities and excess whitespace from a mail snippet before embedding. */
export function cleanSnippet(raw: string): string {
  return raw
    .replace(/https?:\/\/\S+/g, '')
    .replace(/&[a-z#0-9]+;/gi, ' ')
    .replace(/&\w*$/, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 300);
}

/** @deprecated Only kept for unit tests — no longer used by MailModelManager. */
export function extractGeneratedText(generated_text: unknown): string {
  if (Array.isArray(generated_text)) {
    const last = generated_text.at(-1);
    return (last as { content?: string } | undefined)?.content ?? '';
  }
  if (typeof generated_text === 'string') return generated_text;
  return '';
}

/** @deprecated Only kept for unit tests — no longer used by MailModelManager. */
export function parseTags(raw: string): string[] {
  return raw
    .split(',')
    .map((t) => t.trim().replace(/^["'\s]+|["'\s]+$/g, ''))
    .filter((t) => t.length > 0 && t.length <= 50 && !t.includes('\n') && !t.includes(':') && !/[€$£¥]|\d/.test(t))
    .slice(0, 3);
}

export const mailModelManager = new MailModelManager();
